import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  toGridFilterStartOfDay,
  toGridFilterEndOfDay,
  calculateDateRange,
  getGridDateTimeMode,
} from '../../utils';
import type { DateFilterRendererProps } from './DateRangeFilter';
import { CustomDatePicker, DATE_PICKER_MODE } from '../../../../components/common/CustomDatePicker';

export const MonthFilterRenderer = ({
  command,
  onDateRangeChange,
  initialDateRange,
}: DateFilterRendererProps) => {
  const dateTimeMode = getGridDateTimeMode(command.settings);

  const toIsoRange = (date: Date) => {
    const month = dayjs(date);
    return {
      start: toGridFilterStartOfDay(month.startOf('month'), dateTimeMode),
      end: toGridFilterEndOfDay(month.endOf('month'), dateTimeMode),
    };
  };

  const initialMonth = useMemo(() => {
    if (initialDateRange?.startDate) {
      return dayjs(initialDateRange.startDate).startOf('month').toDate();
    }
    if (command.defaultRange) {
      const { startDate } = calculateDateRange(command.defaultRange);
      return dayjs(startDate).startOf('month').toDate();
    }
    return dayjs().startOf('month').toDate();
  }, [initialDateRange?.startDate, command.defaultRange]);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(initialMonth);

  useEffect(() => {
    setSelectedMonth(initialMonth);
  }, [initialMonth]);

  const handleMonthChange = (value: Date | null) => {
    setSelectedMonth(value);
    if (value) {
      const { start, end } = toIsoRange(value);
      onDateRangeChange(start, end);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', minWidth: 220 }}>
      <CustomDatePicker
        label="Month"
        value={selectedMonth}
        mode={DATE_PICKER_MODE.MONTH}
        onChange={handleMonthChange}
      />
    </Box>
  );
};
